import React, { Fragment, PureComponent } from 'react';
import { CoordinationState, CoordinationSummary, SolutionState } from 'app/model/SOL49';
import { Button, Col, Divider, Row } from 'antd';
import { ForwardOutlined } from '@ant-design/icons';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { Flex } from 'app/components/common/Flex';
import './CoordinationSummaryNotification.less';
import { CoordinationUtils, SolutionsStatusMap } from 'app/common/util/CoordinationUtils';
import Utils from 'app/common/util/Utils';
import { SolutionUtils } from '../../../common/util/SolutionUtils';

interface CoordinationSummaryNotificationProps {
    coordinationSummary: CoordinationSummary;
    diff: SolutionsStatusMap;
    newMessage: boolean;
    onOpenCoordination: (coordination: CoordinationSummary) => void;
}

export class CoordinationSummaryNotification extends PureComponent<CoordinationSummaryNotificationProps> {
    renderTFVs = (coordination: CoordinationSummary) => {
        const fmps = Object.keys(coordination.status);
        const trafficVolumes = Utils.removeDuplicates(coordination.solutions.flatMap(s => s.tvs));

        return (
            <>
                {trafficVolumes.map((tv, idx) => (
                    <div key={tv}>
                        <ConditionalRender condition={idx !== 0}>
                            <Divider type="horizontal" />
                        </ConditionalRender>
                        <span>{tv}</span>
                    </div>
                ))}
                <ConditionalRender condition={Utils.size(fmps) === 1}>
                    <span className="fmp">{fmps[0]}</span>
                </ConditionalRender>
            </>
        );
    };

    renderSolutions = () => {
        const coordination = this.props.coordinationSummary;
        const impactedSolutions = coordination.solutions.filter(
            s =>
                Utils.isNotExists(this.props.diff) ||
                Object.keys(this.props.diff).length === 0 ||
                s.name in this.props.diff,
        );

        return (
            <Flex direction="vertical">
                {impactedSolutions.map((s, idx) => {
                    return (
                        <div key={s.name}>
                            <ConditionalRender condition={idx !== 0}>
                                <Divider type="horizontal" />
                            </ConditionalRender>
                            <span>{s.name}</span>
                        </div>
                    );
                })}
            </Flex>
        );
    };

    renderCoordinationStates = () => {
        const coordination = this.props.coordinationSummary;
        const impactedSolutions = coordination.solutions.filter(
            s => Utils.isNotExists(this.props.diff) || s.name in this.props.diff,
        );
        const hasMultipleFmps = Object.keys(coordination.status).length > 1;

        // New coordination notification
        if (Utils.isNotExists(this.props.diff) && !this.props.newMessage) {
            return <div>New Coordination</div>;
        } else if (this.props.newMessage) {
            return <div>New Message</div>;
        }

        return (
            <Flex direction="vertical">
                {impactedSolutions.map((s, solIdx) => {
                    return (
                        <div key={s.name}>
                            <ConditionalRender condition={solIdx !== 0}>
                                <Divider type="horizontal" />
                            </ConditionalRender>
                            <Flex>
                                {Utils.isExists(this.props.diff?.[s.name]) &&
                                    Object.entries(this.props.diff[s.name]).map(([state, fmps], idx) => {
                                        const stateShortString = CoordinationUtils.toSolutionStateShortString(
                                            state as SolutionState,
                                        );
                                        const concernedFmps = fmps.length > 1 ? fmps.length : fmps[0];
                                        const result = `${stateShortString} ${hasMultipleFmps ? '(' + concernedFmps + ')' : ''}`;
                                        return (
                                            <Fragment key={state}>
                                                <ConditionalRender condition={idx !== 0}>
                                                    <div className="divider-container" style={{ padding: '0 0.5em' }}>
                                                        <Divider type="vertical" className="divider" />
                                                    </div>
                                                </ConditionalRender>
                                                <span>{result}</span>
                                            </Fragment>
                                        );
                                    })}
                            </Flex>
                        </div>
                    );
                })}
            </Flex>
        );
    };

    render() {
        const coordination = this.props.coordinationSummary;

        const state = coordination.state === CoordinationState.ONGOING ? 'OPEN' : coordination.state;
        const status = this.props.diff
            ? 'NEW UPDATE'
            : this.props.newMessage
              ? 'NEW MESSAGE'
              : coordination.state === CoordinationState.ONGOING
                ? 'NEW COORDINATION'
                : coordination.state;
        const origin = coordination.solutions.some(SolutionUtils.isInitialSolution) ? (
            <span>FMP</span>
        ) : (
            <span>NM</span>
        );

        return (
            <div key={coordination.id}>
                <div className="header">
                    <i>{status}</i>
                </div>
                <Row className="content" wrap={false}>
                    <Col flex="auto" className="origin">
                        {origin}
                    </Col>
                    <div className="divider-container">
                        <Divider type="vertical" className="divider" />
                    </div>
                    <Col flex="auto">{this.renderTFVs(coordination)}</Col>
                    <div className="divider-container">
                        <Divider type="vertical" className="divider" />
                    </div>
                    <Col flex="auto">{this.renderSolutions()}</Col>
                    <div className="divider-container">
                        <Divider type="vertical" className="divider" />
                    </div>
                    <Col flex="auto">{this.renderCoordinationStates()}</Col>
                    <div className="divider-container">
                        <Divider type="vertical" className="divider" />
                    </div>
                    <Col flex="auto">
                        <Button
                            className="status-button"
                            onClick={() => {
                                this.props.onOpenCoordination(coordination);
                                CoordinationUtils.onOpenCoordination(coordination, this.props.onOpenCoordination);
                            }}>
                            {state}
                            <ForwardOutlined />
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    }
}
